const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:19001';

/**
 * Failure of an HTTP request. Carries the response status so callers can tell a
 * definitive rejection (401) apart from a transient one (5xx, gateway error,
 * network failure).
 */
export class ApiError extends Error {
  /** HTTP status of the failed response. `declare` on purpose: no class field is
   *  emitted, so the value assigned in the constructor cannot be shadowed by
   *  field initialization order under any emit target. */
  declare readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Builds the rejection for a non-ok response: the server's `message` when the
 * body is well-formed JSON, otherwise the endpoint's fallback text. The body is
 * read once as text and parsed inside a guard, so an empty body, an nginx 502
 * HTML page or a truncated response yields the fallback instead of a raw
 * `SyntaxError: Unexpected end of JSON input`.
 */
const apiError = async (
  response: Response,
  fallback: string,
): Promise<ApiError> => {
  let body: string | null = null;
  try {
    body = await response.text();
  } catch {
    // The body itself could not be read; treat it as absent.
    body = null;
  }

  let serverMessage: string | null = null;
  if (body) {
    try {
      const parsed: unknown = JSON.parse(body);
      const message = (parsed as { message?: unknown } | null)?.message;
      if (typeof message === 'string' && message) {
        serverMessage = message;
      }
    } catch {
      // Not JSON: keep the endpoint's fallback message.
    }
  }

  return new ApiError(serverMessage || fallback, response.status);
};

export interface OAuth2AuthorizeResponse {
  authorization_url: string;
  state: string;
  code_verifier: string;
}

export interface OAuth2User {
  sub: string;
  email?: string;
  name?: string;
}

export interface OAuth2CallbackResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  user?: OAuth2User;
  error?: string;
  message?: string;
  isAdmin?: boolean;
  isGlobalAdmin?: boolean;
  organizationCodes?: string[];
}

export interface Organization {
  id: string;
  code: string;
  name: string;
}

export interface Tenant {
  tenant_id: string;
  organization_id: string;
  invoice_prefix: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreateTenantDto {
  organization_id: string;
  is_active?: boolean;
  invoice_prefix?: string;
}

export interface UpdateTenantDto {
  organization_id?: string;
  is_active?: boolean;
  invoice_prefix?: string;
}

export interface PaymentMethod {
  payment_method_id: string;
  tenant_id: string;
  payment_method_name: string;
  display_name: string | null;
  description: string | null;
  settings: Record<string, any>;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreatePaymentMethodDto {
  tenant_id: string;
  payment_method_name: string;
  display_name?: string;
  description?: string;
  settings?: Record<string, any>;
  display_order?: number;
  is_active?: boolean;
}

export interface UpdatePaymentMethodDto {
  payment_method_name?: string;
  display_name?: string;
  description?: string;
  settings?: Record<string, any>;
  display_order?: number;
  is_active?: boolean;
}

export interface ServiceToken {
  id: string;
  service_name: string;
  description: string | null;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateServiceTokenDto {
  service_name: string;
  description?: string;
}

export interface ServiceTokenCreatedResponse extends ServiceToken {
  token: string;
}

export interface TaxRate {
  tax_rate_id: string;
  tax_rate_name: string;
  rate_percentage: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreateTaxRateDto {
  tax_rate_name: string;
  rate_percentage: number;
  is_active?: boolean;
}

export const authApi = {
  // OAuth2 methods
  async getAuthorizationUrl(): Promise<OAuth2AuthorizeResponse> {
    const response = await fetch(`${API_URL}/auth/oauth2/authorize`);

    if (!response.ok) {
      throw new ApiError('Failed to get authorization URL', response.status);
    }

    return response.json();
  },

  async handleOAuth2Callback(
    code: string,
    state: string,
    codeVerifier: string,
  ): Promise<OAuth2CallbackResponse> {
    const response = await fetch(`${API_URL}/auth/oauth2/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        state,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok && response.status !== 403) {
      throw new ApiError('OAuth2 callback failed', response.status);
    }

    return response.json();
  },

  async oauth2Logout(token: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_URL}/auth/oauth2/logout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Logout failed', response.status);
    }

    return response.json();
  },

  async getOAuth2UserInfo(token: string): Promise<OAuth2User & { isAdmin: boolean }> {
    const response = await fetch(`${API_URL}/auth/oauth2/userinfo`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to get user info', response.status);
    }

    return response.json();
  },

  async validateOAuth2Token(token: string): Promise<{
    valid: boolean;
    sub: string;
    email?: string;
    name?: string;
    isAdmin: boolean;
    isGlobalAdmin?: boolean;
    organizationCodes?: string[];
  }> {
    const response = await fetch(`${API_URL}/auth/oauth2/validate-token`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      throw new ApiError('Token invalid or expired', response.status);
    }

    if (!response.ok) {
      throw new ApiError('Failed to validate token', response.status);
    }

    return response.json();
  },
};

export const tenantsApi = {
  async getAll(token: string): Promise<Tenant[]> {
    const response = await fetch(`${API_URL}/tenants`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch tenants', response.status);
    }

    return response.json();
  },

  async create(token: string, data: CreateTenantDto): Promise<Tenant> {
    const response = await fetch(`${API_URL}/tenants`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to create tenant');
    }

    return response.json();
  },

  async update(token: string, id: string, data: UpdateTenantDto): Promise<Tenant> {
    const response = await fetch(`${API_URL}/tenants/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to update tenant');
    }

    return response.json();
  },

  async delete(token: string, id: string): Promise<void> {
    const response = await fetch(`${API_URL}/tenants/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to delete tenant', response.status);
    }
  },

  async restore(token: string, id: string): Promise<Tenant> {
    const response = await fetch(`${API_URL}/tenants/${id}/restore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to restore tenant', response.status);
    }

    return response.json();
  },

  async getOne(token: string, id: string): Promise<Tenant> {
    const response = await fetch(`${API_URL}/tenants/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch tenant', response.status);
    }

    return response.json();
  },
};

export const organizationsApi = {
  async getAll(token: string): Promise<Organization[]> {
    const response = await fetch(`${API_URL}/organizations`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch organizations', response.status);
    }

    return response.json();
  },

  async getOne(token: string, id: string): Promise<Organization> {
    const response = await fetch(`${API_URL}/organizations/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch organization', response.status);
    }

    return response.json();
  },
};

export const paymentMethodsApi = {
  async getByTenant(token: string, tenantId: string): Promise<PaymentMethod[]> {
    const response = await fetch(`${API_URL}/payment-methods/tenant/${tenantId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch payment methods', response.status);
    }

    return response.json();
  },

  async create(token: string, data: CreatePaymentMethodDto): Promise<PaymentMethod> {
    const response = await fetch(`${API_URL}/payment-methods`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to create payment method');
    }

    return response.json();
  },

  async update(token: string, id: string, data: UpdatePaymentMethodDto): Promise<PaymentMethod> {
    const response = await fetch(`${API_URL}/payment-methods/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to update payment method');
    }

    return response.json();
  },

  async delete(token: string, id: string): Promise<void> {
    const response = await fetch(`${API_URL}/payment-methods/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to delete payment method', response.status);
    }
  },
};

export interface BillingInfo {
  company_name?: string;
  contact_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  postal_code: string;
  country: string;
  tax_id?: string;
}

export interface ContactInfo {
  email: string;
  phone?: string;
}

export interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  tax_rate?: number;
  tax_amount?: number;
}

export interface Invoice {
  invoice_id: string;
  tenant_id: string;
  invoice_number: string;
  currency: string;
  amount: number;
  billing_info: BillingInfo;
  contact_info: ContactInfo;
  line_items: LineItem[];
  status: 'draft' | 'pending' | 'paid' | 'cancelled' | 'invalidated' | 'partially_paid';
  total_paid: number;
  last_payment_at: string | null;
  payment_count: number;
  tax_rate_id: string | null;
  prices_include_tax: boolean;
  tax_rate?: {
    tax_rate_id: string;
    tax_rate_name: string;
    rate_percentage: number;
  };
  notes: string | null;
  created_at: string;
  updated_at: string;
  settlement_url?: string; // Full settlement URL with token
  tenant?: {
    tenant_id: string;
    organization_id: string;
  };
}

export interface CreateInvoiceDto {
  tenant_id: string;
  currency: string;
  amount: number;
  billing_info: BillingInfo;
  contact_info: ContactInfo;
  line_items: LineItem[];
  tax_rate_id?: string;
  prices_include_tax?: boolean;
  notes?: string;
}

export interface UpdateInvoiceDto {
  notes?: string;
  status?: string;
}

export interface DirectDepositSettings {
  iban?: string;
  beneficiary_name?: string;
  bic?: string;
  bank_name?: string;
  bank_address?: string;
  reference_prefix?: string;
  qr_code_enabled?: boolean;
}

export interface SettlementData {
  invoice: {
    invoice_id: string;
    invoice_number: string;
    invoice_code: string;
    currency: string;
    amount: number;
    total_paid: number;
    amount_due: number;
    pending_amount: number;
    billing_info: BillingInfo;
    contact_info: ContactInfo;
    line_items: LineItem[];
    status: string;
    created_at: string;
    prices_include_tax: boolean;
    tax_rate?: {
      tax_rate_id: string;
      tax_rate_name: string;
      rate_percentage: number;
    };
  };
  tenant: {
    tenant_id: string;
    tenant_name: string;
    logo_url: string | null;
    primary_color: string | null;
    accent_color: string | null;
  };
  payment_methods: Array<{
    payment_method_id: string;
    payment_method_name: string;
    display_name: string;
    description: string | null;
    display_order: number;
    settings?: DirectDepositSettings;
  }>;
}

export interface Payment {
  payment_id: string;
  invoice_id: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'chargeback';
  paid_at: string | null;
  transaction_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  payment_method?: {
    payment_method_id: string;
    payment_method_name: string;
    display_name: string;
  };
  invoice?: {
    invoice_id: string;
    invoice_number: string;
  };
}

export interface CreatePaymentDto {
  invoice_id: string;
  payment_method_id: string;
  amount: number;
  transaction_id?: string;
  notes?: string;
}

export interface UpdatePaymentDto {
  status?: 'pending' | 'completed' | 'failed' | 'refunded' | 'chargeback';
  transaction_id?: string;
  notes?: string;
}

export const invoicesApi = {
  async getAll(token: string, tenantId?: string): Promise<Invoice[]> {
    const url = tenantId
      ? `${API_URL}/invoices?tenant_id=${encodeURIComponent(tenantId)}`
      : `${API_URL}/invoices`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch invoices', response.status);
    }

    return response.json();
  },

  async getOne(token: string, id: string): Promise<Invoice> {
    const response = await fetch(`${API_URL}/invoices/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch invoice', response.status);
    }

    return response.json();
  },

  async create(token: string, data: CreateInvoiceDto): Promise<Invoice> {
    const response = await fetch(`${API_URL}/invoices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to create invoice');
    }

    return response.json();
  },

  async update(token: string, id: string, data: UpdateInvoiceDto): Promise<Invoice> {
    const response = await fetch(`${API_URL}/invoices/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to update invoice');
    }

    return response.json();
  },

  async delete(token: string, id: string): Promise<void> {
    const response = await fetch(`${API_URL}/invoices/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to delete invoice', response.status);
    }
  },

  async restore(token: string, id: string): Promise<Invoice> {
    const response = await fetch(`${API_URL}/invoices/${id}/restore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to restore invoice', response.status);
    }

    return response.json();
  },

  async invalidate(token: string, id: string): Promise<Invoice> {
    const response = await fetch(`${API_URL}/invoices/${id}/invalidate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to invalidate invoice', response.status);
    }

    return response.json();
  },

  async resendEmail(token: string, id: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_URL}/invoices/${id}/resend-email`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to resend invoice email');
    }

    return response.json();
  },

  // Public endpoints (no authentication)
  async getSettlementData(invoiceId: string, token: string): Promise<SettlementData> {
    const response = await fetch(`${API_URL}/invoices/settlement/${invoiceId}?token=${encodeURIComponent(token)}`);

    if (!response.ok) {
      throw await apiError(response, 'Failed to fetch settlement data');
    }

    return response.json();
  },

  getSettlementUrl(invoice: Invoice, baseUrl?: string): string {
    // This method won't work in the frontend since we don't have access to settlement_token
    // Instead, we'll provide a way to copy the link from the admin interface
    // The backend will need to send the full settlement URL when creating an invoice
    return `${baseUrl || window.location.origin}/settlement/${invoice.invoice_id}`;
  },

  async getEpcQrCode(invoiceId: string, token: string, paymentMethodId: string): Promise<{ qr_code: string; payment_reference: string }> {
    const response = await fetch(
      `${API_URL}/invoices/settlement/${invoiceId}/qr-code?token=${encodeURIComponent(token)}&payment_method_id=${encodeURIComponent(paymentMethodId)}`
    );

    if (!response.ok) {
      throw await apiError(response, 'Failed to generate QR code');
    }

    return response.json();
  },
};

export const serviceTokensApi = {
  async getAll(token: string): Promise<ServiceToken[]> {
    const response = await fetch(`${API_URL}/admin/service-tokens`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch service tokens', response.status);
    }

    return response.json();
  },

  async getOne(token: string, id: string): Promise<ServiceToken> {
    const response = await fetch(`${API_URL}/admin/service-tokens/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch service token', response.status);
    }

    return response.json();
  },

  async create(token: string, data: CreateServiceTokenDto): Promise<ServiceTokenCreatedResponse> {
    const response = await fetch(`${API_URL}/admin/service-tokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to create service token');
    }

    return response.json();
  },

  async regenerate(token: string, id: string): Promise<ServiceTokenCreatedResponse> {
    const response = await fetch(`${API_URL}/admin/service-tokens/${id}/regenerate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to regenerate service token', response.status);
    }

    return response.json();
  },

  async activate(token: string, id: string): Promise<ServiceToken> {
    const response = await fetch(`${API_URL}/admin/service-tokens/${id}/activate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to activate service token', response.status);
    }

    return response.json();
  },

  async deactivate(token: string, id: string): Promise<ServiceToken> {
    const response = await fetch(`${API_URL}/admin/service-tokens/${id}/deactivate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to deactivate service token', response.status);
    }

    return response.json();
  },

  async delete(token: string, id: string): Promise<void> {
    const response = await fetch(`${API_URL}/admin/service-tokens/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to delete service token', response.status);
    }
  },
};

export const taxRatesApi = {
  async getAll(token: string): Promise<TaxRate[]> {
    const response = await fetch(`${API_URL}/tax-rates`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch tax rates', response.status);
    }

    return response.json();
  },

  async getActive(token: string): Promise<TaxRate[]> {
    const response = await fetch(`${API_URL}/tax-rates/active`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch active tax rates', response.status);
    }

    return response.json();
  },

  async getOne(token: string, id: string): Promise<TaxRate> {
    const response = await fetch(`${API_URL}/tax-rates/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch tax rate', response.status);
    }

    return response.json();
  },

  async create(token: string, data: CreateTaxRateDto): Promise<TaxRate> {
    const response = await fetch(`${API_URL}/tax-rates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to create tax rate');
    }

    return response.json();
  },

  async activate(token: string, id: string): Promise<TaxRate> {
    const response = await fetch(`${API_URL}/tax-rates/${id}/activate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to activate tax rate', response.status);
    }

    return response.json();
  },

  async deactivate(token: string, id: string): Promise<TaxRate> {
    const response = await fetch(`${API_URL}/tax-rates/${id}/deactivate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to deactivate tax rate', response.status);
    }

    return response.json();
  },

  async delete(token: string, id: string): Promise<void> {
    const response = await fetch(`${API_URL}/tax-rates/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to delete tax rate', response.status);
    }
  },

  async restore(token: string, id: string): Promise<TaxRate> {
    const response = await fetch(`${API_URL}/tax-rates/${id}/restore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new ApiError('Failed to restore tax rate', response.status);
    }

    return response.json();
  },
};

export const paymentsApi = {
  async getAll(token: string, invoiceId?: string): Promise<Payment[]> {
    const url = invoiceId
      ? `${API_URL}/payments?invoice_id=${encodeURIComponent(invoiceId)}`
      : `${API_URL}/payments`;

    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) throw new ApiError('Failed to fetch payments', response.status);
    return response.json();
  },

  async create(token: string, data: CreatePaymentDto): Promise<Payment> {
    const response = await fetch(`${API_URL}/payments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to create payment');
    }

    return response.json();
  },

  async updateStatus(token: string, id: string, data: UpdatePaymentDto): Promise<Payment> {
    const response = await fetch(`${API_URL}/payments/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to update payment');
    }

    return response.json();
  },

  async delete(token: string, id: string): Promise<void> {
    const response = await fetch(`${API_URL}/payments/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) throw new ApiError('Failed to delete payment', response.status);
  },
};

// Settlement Import Types
export interface BankFormat {
  id: string;
  bankName: string;
}

export interface ImportRowResult {
  row_number: number;
  success: boolean;
  invoice_number?: string;
  amount?: number;
  error_reason?: string;
  payment_id?: string;
}

export interface ImportResult {
  total_rows: number;
  successful_imports: number;
  failed_imports: number;
  results: ImportRowResult[];
}

export const settlementImportApi = {
  async getBankFormats(token: string): Promise<BankFormat[]> {
    const response = await fetch(`${API_URL}/settlement-import/bank-formats`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new ApiError('Failed to fetch bank formats', response.status);
    }

    return response.json();
  },

  async importSettlements(
    token: string,
    tenantId: string,
    paymentMethodId: string,
    bankFormatId: string,
    file: File,
  ): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tenant_id', tenantId);
    formData.append('payment_method_id', paymentMethodId);
    formData.append('bank_format_id', bankFormatId);

    const response = await fetch(`${API_URL}/settlement-import`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to import settlements');
    }

    return response.json();
  },
};

// PayPal Types
export interface CreatePayPalOrderResponse {
  order_id: string;
  approval_url: string;
  payment_id: string;
}

export interface CapturePayPalOrderResponse {
  success: boolean;
  capture_id?: string;
  status: 'completed' | 'failed' | 'pending';
  error?: string;
}

export const paypalApi = {
  /**
   * Create a PayPal order for invoice settlement
   * This returns an approval URL to redirect the user to PayPal
   */
  async createOrder(
    invoiceId: string,
    paymentMethodId: string,
    settlementToken: string,
    returnUrl?: string,
    cancelUrl?: string,
  ): Promise<CreatePayPalOrderResponse> {
    const response = await fetch(`${API_URL}/paypal/checkout/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invoice_id: invoiceId,
        payment_method_id: paymentMethodId,
        settlement_token: settlementToken,
        return_url: returnUrl,
        cancel_url: cancelUrl,
      }),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to create PayPal order');
    }

    return response.json();
  },

  /**
   * Capture a PayPal order after user approval
   * This should be called when the user returns from PayPal
   */
  async captureOrder(
    invoiceId: string,
    paypalOrderId: string,
    settlementToken: string,
  ): Promise<CapturePayPalOrderResponse> {
    const response = await fetch(`${API_URL}/paypal/checkout/capture`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invoice_id: invoiceId,
        paypal_order_id: paypalOrderId,
        settlement_token: settlementToken,
      }),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to capture PayPal payment');
    }

    return response.json();
  },

  /**
   * Refund a PayPal payment (admin only)
   */
  async refundPayment(
    token: string,
    paymentId: string,
    reason?: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_URL}/paypal/refund`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        payment_id: paymentId,
        reason,
      }),
    });

    if (!response.ok) {
      throw await apiError(response, 'Failed to refund payment');
    }

    return response.json();
  },
};
